import { useRegister } from '@/hooks/Landing/useRegister'
import { useRegisterStore } from '@/store/useRegisterStore'
import { Box, Drawer, IconButton, Typography } from '@mui/material'
import { IconX } from '@tabler/icons-react'
import React, { useEffect } from 'react'
import { InterestForm } from './components/InterestForm'
import { InterestInfoDisplay } from './components/InterestInfoDisplay'
import { InterestMessage } from './components/InterestMessage'
import { ResetButton } from './components/ResetButton'
import { ThankYouMessage } from './components/ThankYouMessage'
import { UserForm } from './components/UserForm'
import { UserInfoDisplay } from './components/UserInfoDisplay'
import { WelcomeMessage } from './components/WelcomeMessage'
import { ConsentForm } from './components/ConsentForm'
import { ConsentMessage } from './components/ConsentMessage'

export const RegisterDrawer: React.FC = () => {
  const { open: isRegisterDrawerOpen, toggleDrawer } = useRegisterStore()
  const {
    userInfo,
    interests,
    additionalThoughts,
    showWelcome,
    showUserForm,
    showUserInfo,
    showInterestMessage,
    showInterestForm,
    showInterestInfo,
    showConsentMessage,
    showConsentForm,
    showThankYou,
    showError,
    handleUserFormSubmit,
    handleInterestFormSubmit,
    handleConsentFormSubmit,
    handleWelcomeComplete,
    handleUserInfoComplete,
    handleInterestMessageComplete,
    handleInterestInfoComplete,
    handleConsentMessageComplete,
    resetFormStates,
    initializeForm,
  } = useRegister()

  useEffect(() => {
    if (isRegisterDrawerOpen) {
      initializeForm()
    }
  }, [isRegisterDrawerOpen, initializeForm])

  // Handle drawer closing
  const handleClose = () => {
    toggleDrawer()
    setTimeout(() => {
      resetFormStates()
    }, 300)
  }

  const handleReset = () => {
    resetFormStates()
    initializeForm()
  }

  return (
    <Drawer
      anchor="right"
      open={isRegisterDrawerOpen}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 600 },
          maxWidth: '100%',
        },
      }}
    >
      <Box
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          p: 2,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: theme.palette.gradient.primary,
          },
          borderBottom: 1,
          borderBottomColor: 'divider',
          borderBottomStyle: 'solid',
        })}
      >
        <IconButton
          onClick={handleClose}
          edge="start"
          sx={{
            color: 'text.primary',
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
        >
          <IconX size={20} stroke={1.5} />
        </IconButton>
        <Typography variant="h6" sx={{ ml: 2 }}>
          Disaster Risk Analytics Explorer Registration
        </Typography>
      </Box>

      <Box
        sx={{
          height: '100%',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          p: 1,
        }}
      >
        {/* Welcome Message */}
        {showWelcome && <WelcomeMessage onAnimationComplete={handleWelcomeComplete} />}

        {/* User Form */}
        {showUserForm && <UserForm onFormSubmit={handleUserFormSubmit} />}

        {/* User Info Display */}
        {showUserInfo && <UserInfoDisplay userInfo={userInfo} onAnimationComplete={handleUserInfoComplete} />}

        {/* Interest Message */}
        {showInterestMessage && (
          <InterestMessage userName={userInfo.name} onAnimationComplete={handleInterestMessageComplete} />
        )}

        {/* Interest Form */}
        {showInterestForm && <InterestForm interests={interests} onFormSubmit={handleInterestFormSubmit} />}

        {/* Interest Info Display */}
        {showInterestInfo && (
          <InterestInfoDisplay
            interests={interests}
            additionalThoughts={additionalThoughts}
            onAnimationComplete={handleInterestInfoComplete}
          />
        )}

        {/* Consent Message */}
        {showConsentMessage && <ConsentMessage onAnimationComplete={handleConsentMessageComplete} />}

        {/* Consent Form */}
        {showConsentForm && <ConsentForm interests={interests} onFormSubmit={handleConsentFormSubmit} />}

        {/* Thank You Message */}
        {showThankYou && <ThankYouMessage userName={userInfo.name} />}

        {showError && (
          <Typography variant="body2" color="error" sx={{ mt: 1, mx: 'auto', mb: 1 }}>
            Something went wrong. Please try again.
          </Typography>
        )}
      </Box>

      {/* Reset Button - Only show after completion */}
      {(showThankYou || showError) && <ResetButton onReset={handleReset} />}
    </Drawer>
  )
}
